/**
 * LabelPrinterFields — Impresora de etiquetas de pesaje (carnicería / venta por
 * peso): modo (misma/separada/off) y conexión de la segunda térmica.
 *
 * QUÉ EXPORTA: LabelPrinterFields (named).
 */

import {
  AlertTriangle,
  Bluetooth,
  Hash,
  Loader2,
  Network,
  Printer,
  Usb,
  Wifi,
} from "lucide-react";
import type {
  LabelPrinterMode,
  PosPrinterConfig,
  PosPrinterTransport,
} from "@/lib/native/posPrinterConfig";
import { POS_DEFAULT_NETWORK_HOST } from "@/lib/native/posPrinterConfig";
import { MenuSelect } from "@/components/ui/MenuSelect";
import { POS_HARDWARE_SECTION_LABEL } from "../utils/userSettingsPosHardware.helpers";

interface LabelPrinterFieldsProps {
  cfg: PosPrinterConfig;
  onLabelModeChange: (mode: LabelPrinterMode) => void;
  onLabelChange: (patch: Partial<PosPrinterConfig["labelPrinter"]>) => void;
  isNative: boolean;
  isDesktop: boolean;
  transportOptions: { value: string; label: string; hint: string }[];
  labelModeOptions: { value: string; label: string; hint: string }[];
  labelTestBusy: boolean;
  labelPrinterConfigured: boolean;
  onTestLabel: () => void;
}

export function LabelPrinterFields({
  cfg,
  onLabelModeChange,
  onLabelChange,
  isNative,
  isDesktop,
  transportOptions,
  labelModeOptions,
  labelTestBusy,
  labelPrinterConfigured,
  onTestLabel,
}: LabelPrinterFieldsProps) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-slate-600 p-3 space-y-3">
      <div>
        <p className={POS_HARDWARE_SECTION_LABEL}>
          Impresora de etiquetas de pesaje
        </p>
        <div className="relative">
          <span className="abs-center-y left-3 z-[1] pointer-events-none text-primary-600">
            <Printer size={14} />
          </span>
          <MenuSelect
            value={cfg.labelPrinterMode}
            options={labelModeOptions}
            onChange={(v) => onLabelModeChange(v as LabelPrinterMode)}
            className="[&>button]:pl-9"
          />
        </div>
        <p className="mt-1 text-[10px] text-gray-500 dark:text-slate-400 leading-relaxed px-0.5">
          {cfg.labelPrinterMode === "same" &&
            "El botón «Imprimir etiqueta» del modal de peso usa la impresora de recibos (ponle rollo adhesivo de 58 mm)."}
          {cfg.labelPrinterMode === "separate" &&
            "Las etiquetas se imprimen en una segunda impresora (etiquetas adhesivas). Los recibos siguen en la principal."}
          {cfg.labelPrinterMode === "off" &&
            "El botón «Imprimir etiqueta» no aparece en el modal de peso."}
        </p>
      </div>

      {cfg.labelPrinterMode === "separate" && (
        <>
          <div>
            <p className={POS_HARDWARE_SECTION_LABEL}>
              Conexión de la impresora de etiquetas
            </p>
            <div className="relative">
              <span className="abs-center-y left-3 z-[1] pointer-events-none text-primary-600">
                <Printer size={14} />
              </span>
              <MenuSelect
                value={cfg.labelPrinter.transport}
                options={transportOptions}
                onChange={(v) =>
                  onLabelChange({ transport: v as PosPrinterTransport })
                }
                className="[&>button]:pl-9"
              />
            </div>
            {isNative && (
              <p className="mt-1.5 flex items-start gap-1.5 px-1 text-[10px] text-amber-600 dark:text-amber-400 leading-snug">
                <AlertTriangle size={11} className="shrink-0 mt-px" />
                En la APK la etiqueta se imprime en la impresora configurada de
                la app. Para usar una segunda impresora, usa la web o la app de
                escritorio.
              </p>
            )}
          </div>

          {cfg.labelPrinter.transport === "network" && (
            <div className="space-y-2">
              <div className="relative">
                <Wifi size={16} className="abs-center-y left-3 text-gray-400" />
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder={POS_DEFAULT_NETWORK_HOST}
                  value={cfg.labelPrinter.networkHost}
                  onChange={(e) =>
                    onLabelChange({ networkHost: e.target.value.trim() })
                  }
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-semibold"
                />
              </div>
              <div className="relative">
                <Hash size={16} className="abs-center-y left-3 text-gray-400" />
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="9100"
                  value={cfg.labelPrinter.networkPort || ""}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    onLabelChange({
                      networkPort: Number.isFinite(v)
                        ? Math.min(65535, Math.max(1, v))
                        : 9100,
                    });
                  }}
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-semibold"
                />
              </div>
              <div className="relative">
                <Network
                  size={16}
                  className="abs-center-y left-3 text-gray-400"
                />
                <input
                  type="text"
                  inputMode="url"
                  autoComplete="off"
                  placeholder="http://192.168.1.50:8765"
                  value={cfg.labelPrinter.networkRelayUrl}
                  onChange={(e) =>
                    onLabelChange({ networkRelayUrl: e.target.value.trim() })
                  }
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-mono"
                />
              </div>
            </div>
          )}

          {cfg.labelPrinter.transport === "usb" && (
            <div className="space-y-2">
              <div className="relative">
                <Usb size={16} className="abs-center-y left-3 text-gray-400" />
                <input
                  type="text"
                  autoComplete="off"
                  placeholder="Puerto USB (ej. USB001)"
                  value={cfg.labelPrinter.usbDeviceKey}
                  onChange={(e) =>
                    onLabelChange({
                      usbDeviceKey: e.target.value.trim().toUpperCase(),
                    })
                  }
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-mono"
                />
              </div>
              <p className="px-0.5 text-[10px] text-gray-500 dark:text-slate-400 leading-snug">
                {isDesktop
                  ? "Conecta la impresora de etiquetas por USB al PC y escribe el puerto (ej. USB001), como en la principal."
                  : "En web se usa WebUSB (Chrome/Edge con HTTPS): escribe el ID vendorId:productId."}
              </p>
            </div>
          )}

          {cfg.labelPrinter.transport === "bluetooth_spp" && (
            <div className="space-y-2">
              <div className="relative">
                <Bluetooth
                  size={16}
                  className="abs-center-y left-3 text-gray-400"
                />
                <input
                  type="text"
                  autoComplete="off"
                  placeholder="Puerto COM o MAC (ej. COM4)"
                  value={cfg.labelPrinter.bluetoothMac}
                  onChange={(e) =>
                    onLabelChange({
                      bluetoothMac: e.target.value.trim().toUpperCase(),
                    })
                  }
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-mono"
                />
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onTestLabel}
            disabled={labelTestBusy || !labelPrinterConfigured}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-primary-600 text-primary-700 dark:text-primary-300 font-black text-[10px] uppercase tracking-wide disabled:opacity-40"
          >
            {labelTestBusy ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Printer size={14} />
            )}
            Imprimir etiqueta de prueba
          </button>
        </>
      )}
    </div>
  );
}
